/**
 * Tenant Create Page
 *
 * Form for creating a new tenant with initial settings.
 * Capability-gated to users with admin:tenants capability.
 */

import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import type { ReactElement, FormEvent, ChangeEvent } from "react";
import { useState, useCallback } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { apiRequest } from "../../../api/client";
import { useAuthStore } from "../../../store/authStore";

import type { TenantCreateInput, TenantFeatures, TenantQuotas, Tenant } from "./types";
import {
  DEFAULT_TENANT_FEATURES,
  DEFAULT_TENANT_QUOTAS,
  FEATURE_LABELS,
  FEATURE_DESCRIPTIONS,
} from "./types";

interface FormState {
  name: string;
  slug: string;
  features: TenantFeatures;
  quotas: TenantQuotas;
}

export function TenantCreatePage(): ReactElement {
  const { hasCapability } = useAuthStore();

  // Check capability
  if (!hasCapability("admin:tenants")) {
    return <Navigate to="/studio" replace />;
  }

  return <TenantCreateContent />;
}

function TenantCreateContent(): ReactElement {
  const navigate = useNavigate();
  const [formState, setFormState] = useState<FormState>({
    name: "",
    slug: "",
    features: { ...DEFAULT_TENANT_FEATURES },
    quotas: { ...DEFAULT_TENANT_QUOTAS },
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation({
    mutationFn: async (input: TenantCreateInput) => {
      const response = await apiRequest<Tenant>("/api/v1/admin/tenants", {
        method: "POST",
        body: input,
      });

      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: (tenant) => {
      navigate(`/studio/admin/tenants/${tenant.externalId}`);
    },
  });

  const handleNameChange = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
    const name = e.target.value;
    setFormState((prev) => ({
      ...prev,
      name,
      // Auto-generate slug from name
      slug: prev.slug || generateSlug(name),
    }));
    setErrors((prev) => ({ ...prev, ["name"]: "" }));
  }, []);

  const handleSlugChange = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
    const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-");
    setFormState((prev) => ({ ...prev, slug }));
    setErrors((prev) => ({ ...prev, ["slug"]: "" }));
  }, []);

  const handleFeatureChange = useCallback((feature: keyof TenantFeatures): void => {
    setFormState((prev) => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: !prev.features[feature],
      },
    }));
  }, []);

  const handleQuotaChange = useCallback((quota: keyof TenantQuotas, value: string): void => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      setFormState((prev) => ({
        ...prev,
        quotas: {
          ...prev.quotas,
          [quota]: numValue,
        },
      }));
    }
  }, []);

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formState.name.trim()) {
      newErrors["name"] = "Name is required";
    } else if (formState.name.length < 2) {
      newErrors["name"] = "Name must be at least 2 characters";
    } else if (formState.name.length > 100) {
      newErrors["name"] = "Name must be less than 100 characters";
    }

    if (!formState.slug.trim()) {
      newErrors["slug"] = "Slug is required";
    } else if (!/^[a-z0-9-]+$/.test(formState.slug)) {
      newErrors["slug"] = "Slug can only contain lowercase letters, numbers, and hyphens";
    } else if (formState.slug.length < 2) {
      newErrors["slug"] = "Slug must be at least 2 characters";
    } else if (formState.slug.length > 50) {
      newErrors["slug"] = "Slug must be less than 50 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formState]);

  const handleSubmit = useCallback(
    (e: FormEvent): void => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      const input: TenantCreateInput = {
        name: formState.name.trim(),
        slug: formState.slug.trim(),
        settings: {
          features: formState.features,
          quotas: formState.quotas,
          allowedEnvironments: ["dev", "staging", "prod"],
        },
      };

      createMutation.mutate(input);
    },
    [formState, validateForm, createMutation]
  );

  return (
    <div className="p-6 max-w-2xl">
      {/* Page header */}
      <div className="mb-6">
        <Link
          to="/studio/admin/tenants"
          className="inline-flex items-center gap-1 text-sm text-foreground-muted hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Tenants
        </Link>
        <h1 className="text-2xl font-bold">Create Tenant</h1>
        <p className="mt-1 text-foreground-muted">
          Set up a new tenant organization with initial configuration
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium mb-1">
                Tenant Name <span className="text-error">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={formState.name}
                onChange={handleNameChange}
                placeholder="e.g., Acme Insurance"
                className={`w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                  errors["name"]
                    ? "border-error focus:border-error focus:ring-error"
                    : "border-border focus:border-primary focus:ring-primary"
                }`}
              />
              {errors["name"] && <p className="mt-1 text-sm text-error">{errors["name"]}</p>}
            </div>

            {/* Slug */}
            <div>
              <label htmlFor="slug" className="block text-sm font-medium mb-1">
                Slug <span className="text-error">*</span>
              </label>
              <input
                id="slug"
                type="text"
                value={formState.slug}
                onChange={handleSlugChange}
                placeholder="e.g., acme-insurance"
                className={`w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                  errors["slug"]
                    ? "border-error focus:border-error focus:ring-error"
                    : "border-border focus:border-primary focus:ring-primary"
                }`}
              />
              <p className="mt-1 text-xs text-foreground-muted">
                Used in URLs and API calls. Only lowercase letters, numbers, and hyphens.
              </p>
              {errors["slug"] && <p className="mt-1 text-sm text-error">{errors["slug"]}</p>}
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Features</h2>
          <p className="text-sm text-foreground-muted mb-4">
            Enable or disable features for this tenant
          </p>
          <div className="space-y-3">
            {(Object.keys(formState.features) as (keyof TenantFeatures)[]).map((feature) => (
              <label key={feature} className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formState.features[feature]}
                  onChange={() => handleFeatureChange(feature)}
                  className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
                <div>
                  <p className="text-sm font-medium">{FEATURE_LABELS[feature]}</p>
                  <p className="text-xs text-foreground-muted">{FEATURE_DESCRIPTIONS[feature]}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Quotas */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Quotas</h2>
          <p className="text-sm text-foreground-muted mb-4">Set resource limits for this tenant</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="maxVerticals" className="block text-sm font-medium mb-1">
                Max Verticals
              </label>
              <input
                id="maxVerticals"
                type="number"
                min="1"
                value={formState.quotas.maxVerticals}
                onChange={(e) => handleQuotaChange("maxVerticals", e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label htmlFor="maxProductsPerVertical" className="block text-sm font-medium mb-1">
                Max Products per Vertical
              </label>
              <input
                id="maxProductsPerVertical"
                type="number"
                min="1"
                value={formState.quotas.maxProductsPerVertical}
                onChange={(e) => handleQuotaChange("maxProductsPerVertical", e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label htmlFor="maxUsersPerTenant" className="block text-sm font-medium mb-1">
                Max Users
              </label>
              <input
                id="maxUsersPerTenant"
                type="number"
                min="1"
                value={formState.quotas.maxUsersPerTenant}
                onChange={(e) => handleQuotaChange("maxUsersPerTenant", e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label htmlFor="maxApiRequestsPerDay" className="block text-sm font-medium mb-1">
                Max API Requests/Day
              </label>
              <input
                id="maxApiRequestsPerDay"
                type="number"
                min="0"
                value={formState.quotas.maxApiRequestsPerDay}
                onChange={(e) => handleQuotaChange("maxApiRequestsPerDay", e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label htmlFor="maxStorageMb" className="block text-sm font-medium mb-1">
                Max Storage (MB)
              </label>
              <input
                id="maxStorageMb"
                type="number"
                min="0"
                value={formState.quotas.maxStorageMb}
                onChange={(e) => handleQuotaChange("maxStorageMb", e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>
        </div>

        {/* Error message */}
        {createMutation.isError && (
          <div className="rounded-md border border-error bg-error/10 p-4">
            <p className="text-sm text-error">
              {createMutation.error instanceof Error
                ? createMutation.error.message
                : "Failed to create tenant"}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Link to="/studio/admin/tenants" className="btn btn-secondary">
            Cancel
          </Link>
          <button type="submit" disabled={createMutation.isPending} className="btn btn-primary">
            {createMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Create Tenant
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Generate a URL-friendly slug from a name.
 */
function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50);
}
